import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatNotificationTime } from '../utils/formatUtils';
import Button from './Button';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

/**
 * Notification box component that displays a list of notifications
 * @param {Object} props
 * @param {Array} props.notifications - List of notification items
 * @param {Function} props.onViewAll - Function called when "View All" is clicked
 * @param {Function} props.onNotificationClick - Function called when a notification is clicked
 * @param {Function} props.onLoadMore - Function called when "Load More" is clicked
 * @param {boolean} props.hasMore - Whether there are more notifications to load
 * @param {boolean} props.isLoading - Whether more notifications are being loaded
 * @param {string} props.className - Additional CSS classes
 */
const NotificationBox = ({
  notifications,
  onViewAll,
  onNotificationClick,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  className
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Ensure notifications is always an array
  const safeNotifications = notifications || [];





  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }

    // Handle navigation based on notification type
    if (notification.navigation) {
      const { type, resourceId, parameters } = notification.navigation;
      
      switch (type) {
        case 'ORDER':
          if (resourceId) {
            if (notification.category === 'PURCHASE') {
              // Navigate to orders page with order ID in state
              navigate('/me/orders', { state: { openOrderId: resourceId } });
            } else if (notification.category === 'SALE') {
              // Navigate to sales page with sale ID in state
              navigate('/me/sales', { state: { openSaleId: resourceId } });
            }
          }
          break;
        case 'LISTING':
          if (resourceId) {
            navigate(`/listing/edit/${resourceId}`);
          }
          break;
        case 'WALLET':
          navigate('/me/wallet');
          break;
        case 'PROFILE':
          navigate('/me');
          break;
        case 'EXTERNAL_LINK':
          if (parameters?.url) {
            window.open(parameters.url, '_blank');
          }
          break;
        case 'NONE':
        default:
          // Do nothing for NONE type
          break;
      }
    }
  };

  return (
    <div className={`h-[370px] p-4 bg-white rounded-md shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-[#dddddd] flex flex-col justify-start items-start gap-4 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="self-stretch flex justify-between items-start">
        <div className="text-[#aaaaaa] text-sm font-normal">
          {t('notifications.message_center')}
        </div>
        <div 
          className="text-right text-[#2966ff] text-xs font-normal cursor-pointer hover:underline"
          onClick={onViewAll}
        >
          {t('notifications.view_all')}
        </div>
      </div>

      {/* Notification List */}
      <div className="self-stretch h-[318px] pb-4 flex flex-col justify-start items-center gap-4 overflow-y-auto">
        {isLoading && safeNotifications.length === 0 ? (
          <div className="w-full flex justify-center items-center py-8">
            <p className="text-[#aaaaaa] text-xs">{t('common.loading')}</p>
          </div>
        ) : safeNotifications.length === 0 ? (
          <div className="w-full flex justify-center items-center py-8">
            <p className="text-[#aaaaaa] text-xs">{t('notifications.no_notifications') || 'No notifications'}</p>
          </div>
        ) : (
          <div className="w-full flex flex-col justify-start items-start gap-4 pt-1">
            {safeNotifications.map((notification) => (
            <div 
              key={notification.id}
              className="self-stretch relative flex justify-start items-center gap-3 cursor-pointer hover:bg-[#f4f4f4] p-1 rounded"
              onClick={() => handleNotificationClick(notification)}
            >
              {/* Notification Image */}
              <div className="w-14 h-14 relative bg-white border border-[#DDDDDD] rounded overflow-hidden flex-shrink-0">
                <img 
                  className="w-14 h-14 object-cover" 
                  src={getProductThumbnailUrl(notification.image?.url)} 
                  alt=""
                />
              </div>

              {/* Notification Content */}
              <div className="flex-1 flex flex-col justify-start items-start gap-0">
                <div className="self-stretch text-[#111111] text-sm font-normal leading-[21px] line-clamp-2">
                  {notification.localizedTitle}
                </div>
                <div className="self-stretch flex justify-start items-center gap-2">
                  <div className="flex-1 text-[#aaaaaa] text-xs font-normal leading-[18px] line-clamp-1">
                    {notification.localizedContent}
                  </div>
                  <div className="flex-shrink-0 text-right text-[#aaaaaa] text-xs font-normal leading-[18px] whitespace-nowrap">
                    {formatNotificationTime(notification.createdAt)}
                  </div>
                </div>
              </div>

              {/* Unread indicator */}
              {!notification.isRead && (
                <div className="w-2 h-2 absolute left-[50px] top-[-2px] bg-[#ff5501] rounded outline outline-2 outline-white" />
              )}
            </div>
            ))}
          </div>
        )}

        {/* Load More Button - Only show when loading more (not initial load) */}
        {isLoading && safeNotifications.length > 0 && (
          <div className="w-full flex justify-center items-center py-2">
            <p className="text-[#aaaaaa] text-xs">{t('common.loading')}</p>
          </div>
        )}

        {hasMore && !isLoading && onLoadMore && (
          <div className="w-full flex justify-center py-2">
            <Button
              variant="default"
              size="s"
              onClick={onLoadMore}
              disabled={isLoading}
            >
              {t('home.actions.load_more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationBox;
